import React, {useState, useEffect} from 'react';
import AppBar from "../components/app_bar/AppBar";
import Drawer from "../components/Drawer";
import { 
    Typography, CssBaseline, Box, Toolbar, Grid, Card, CardContent, 
    useMediaQuery, Container, Paper, Stack, Chip, Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useParams } from "react-router-dom";
import { 
    People as PeopleIcon, 
    Groups as GroupsIcon, 
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

import * as CONSTANTS from "../constants"

const Home = () => {

    document.title = CONSTANTS.TITLE

    let {lang} = useParams();

    const [stat, setStat] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleDrawerOpen = (open) => {
        if (open === undefined) {
            setDrawerOpen(!drawerOpen)
        } else {
            setDrawerOpen(open);
        }
    }

    // fetch data
    const fetchStat = async () => {
        try {
            const response = await fetch(`/api/statistic/home`, {
                method: "POST",
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
            });
            const json = await response.json();
            if (response.status === 200) {
                setStat(json);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    useEffect(()=>{
        fetchStat();
    }, [])

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Statistic Card Component
    const StatCard = ({ icon: Icon, value, label, color = "primary" }) => (
        <Card 
            elevation={2}
            sx={{ 
                height: '100%',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                }
            }}
        >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Box sx={{ mb: 2 }}>
                    <Icon 
                        sx={{ 
                            fontSize: 48, 
                            color: `${color}.main`,
                            mb: 1
                        }} 
                    />
                </Box>
                <Typography 
                    variant="h3" 
                    color={color}
                    sx={{ 
                        fontWeight: 'bold',
                        mb: 1,
                        fontSize: isMobile ? '2rem' : '3rem'
                    }}
                >
                    {value}
                </Typography>
                <Typography 
                    variant="h6" 
                    color="text.secondary"
                    sx={{ 
                        fontSize: isMobile ? '1rem' : '1.25rem',
                        fontWeight: 500
                    }}
                >
                    {label}
                </Typography>
            </CardContent>
        </Card>
    );

    // Hero Section Component
    const HeroSection = () => (
        <Box
            sx={{
                position: 'relative',
                height: isMobile ? '60vh' : '70vh',
                minHeight: isMobile ? '400px' : '500px',
                width: "100%",
                backgroundImage: 'url(/assets/home/home-banner.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 4,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    zIndex: 1,
                }
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ textAlign: 'center', color: 'white' }}>
                    <Typography
                        variant={isMobile ? "h3" : "h2"}
                        sx={{ 
                            fontWeight: 'bold', 
                            mb: 2,
                            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                            lineHeight: 1.2
                        }}
                    >
                        {CONSTANTS.HOME.HERO_TITLE[lang]}
                    </Typography>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{ 
                            mb: 3,
                            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                            maxWidth: '600px',
                            mx: 'auto',
                            lineHeight: 1.4
                        }}
                    >
                        {CONSTANTS.HOME.HERO_SUBTITLE[lang]}
                    </Typography>
                    <Stack 
                        direction={isMobile ? "column" : "row"} 
                        spacing={2} 
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Chip 
                            label={CONSTANTS.HOME.DISCOVER_WAZAREN[lang]} 
                            color="primary" 
                            variant="filled"
                            sx={{ fontSize: '1rem', py: 1 }}
                        />
                        <Chip 
                            label={CONSTANTS.HOME.FIND_WOTAGEISHI[lang]} 
                            color="secondary" 
                            variant="filled"
                            sx={{ fontSize: '1rem', py: 1 }}
                        />
                        <Chip 
                            label={CONSTANTS.HOME.JOIN_ORGANIZATIONS[lang]} 
                            color="info" 
                            variant="filled"
                            sx={{ fontSize: '1rem', py: 1 }}
                        />
                    </Stack>
                </Box>
            </Container>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <CssBaseline />
            <AppBar lang={lang} handleDrawerOpen={handleDrawerOpen}/>
            <Box sx={{ display: 'flex' }}>
                <Drawer lang={lang} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
                <Box component="main" sx={{ flexGrow: 1 }}>
                    <Toolbar />
                    <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4 }}>
                        {/* Hero Section */}
                        <HeroSection />

                        {/* Statistics Section */}
                        <Box sx={{ mb: 6 }}>
                            <Typography
                                variant={isMobile ? "h4" : "h3"}
                                sx={{ 
                                    fontWeight: 'bold',
                                    mb: 4,
                                    textAlign: 'center',
                                    color: 'text.primary'
                                }}
                            >
                                {CONSTANTS.HOME.WE_RECORDED[lang]}
                            </Typography>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <StatCard
                                        icon={PeopleIcon}
                                        value={stat?.wazaren || "---"}
                                        label={CONSTANTS.HOME.WAZARENS[lang]}
                                        color="primary"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <StatCard
                                        icon={GroupsIcon}
                                        value={stat?.wotageishi || "---"}
                                        label={CONSTANTS.HOME.WOTAGEISHIS[lang]}
                                        color="secondary"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <StatCard
                                        icon={BusinessIcon}
                                        value={stat?.organization || "---"}
                                        label={CONSTANTS.HOME.ORGANIZATIONS[lang]}
                                        color="info"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Collaboration Section */}
                        <Box sx={{ mb: 6 }}>
                            <Typography
                                variant={isMobile ? "h4" : "h3"}
                                sx={{ 
                                    fontWeight: 'bold',
                                    mb: 4,
                                    textAlign: 'center',
                                    color: 'text.primary'
                                }}
                            >
                                {CONSTANTS.HOME.COLLABORATING_WITH[lang]}
                            </Typography>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={6}>
                                    <Paper 
                                        elevation={2}
                                        sx={{ 
                                            p: 4, 
                                            textAlign: 'center',
                                            height: '100%',
                                            background: `linear-gradient(135deg, ${theme.palette.primary.light}15, ${theme.palette.primary.main}15)`,
                                            border: `1px solid ${theme.palette.primary.light}30`
                                        }}
                                    >
                                        <LocationIcon 
                                            sx={{ 
                                                fontSize: 64, 
                                                color: 'primary.main',
                                                mb: 2
                                            }} 
                                        />
                                        <Typography 
                                            variant="h2" 
                                            color="primary"
                                            sx={{ 
                                                fontWeight: 'bold',
                                                mb: 1,
                                                fontSize: isMobile ? '2.5rem' : '3.5rem'
                                            }}
                                        >
                                            5+
                                        </Typography>
                                        <Typography 
                                            variant="h5" 
                                            color="text.secondary"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {CONSTANTS.HOME.REGIONS[lang]}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6} md={6}>
                                    <Paper 
                                        elevation={2}
                                        sx={{ 
                                            p: 4, 
                                            textAlign: 'center',
                                            height: '100%',
                                            background: `linear-gradient(135deg, ${theme.palette.secondary.light}15, ${theme.palette.secondary.main}15)`,
                                            border: `1px solid ${theme.palette.secondary.light}30`
                                        }}
                                    >
                                        <TrendingUpIcon 
                                            sx={{ 
                                                fontSize: 64, 
                                                color: 'secondary.main',
                                                mb: 2
                                            }} 
                                        />
                                        <Typography 
                                            variant="h2" 
                                            color="secondary"
                                            sx={{ 
                                                fontWeight: 'bold',
                                                mb: 1,
                                                fontSize: isMobile ? '2.5rem' : '3.5rem'
                                            }}
                                        >
                                            30+
                                        </Typography>
                                        <Typography 
                                            variant="h5" 
                                            color="text.secondary"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {CONSTANTS.HOME.ORGANIZATIONS[lang]}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Call to Action Section */}
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography
                                variant={isMobile ? "h5" : "h4"}
                                sx={{ 
                                    mb: 3,
                                    color: 'text.primary',
                                    fontWeight: 500
                                }}
                            >
                                {CONSTANTS.HOME.READY_TO_EXPLORE[lang]}
                            </Typography>
                            <Stack 
                                direction={isMobile ? "column" : "row"} 
                                spacing={2} 
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Button 
                                    variant="contained" 
                                    size="large"
                                    sx={{ 
                                        px: 4, 
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        fontWeight: 600
                                    }}
                                >
                                    {CONSTANTS.HOME.START_EXPLORING[lang]}
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    size="large"
                                    sx={{ 
                                        px: 4, 
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        fontWeight: 600
                                    }}
                                >
                                    {CONSTANTS.HOME.LEARN_MORE[lang]}
                                </Button>
                            </Stack>
                        </Box>
                    </Container>
                </Box>
            </Box>
        </Box>
    );
};
 
export default Home;